import type {
  NotificationChannel,
  NotificationStatus,
  MerchantAttentionNotificationDto,
  MerchantAttentionNotificationPageDto,
} from "@price-rescue/api-contracts";
import type { NotificationEventType } from "@price-rescue/domain";

export type { NotificationChannel, NotificationStatus };
export type { NotificationEventType };
export type MerchantAttentionNotification = MerchantAttentionNotificationDto;
export type MerchantAttentionNotificationPage = MerchantAttentionNotificationPageDto;
export type NotificationPreferenceScope = "STORE" | "ROLE" | "USER";

export interface MerchantNotification {
  notificationId: string;
  storeHash: string;
  recipientUserId: string;
  channel: NotificationChannel;
  status: NotificationStatus;
  eventType: NotificationEventType;
  subject: string;
  bodyText: string;
  bodyHtml?: string;
  createdAt: string;
  deliveryHistory?: NotificationDeliveryAttempt[];
  lastRetryAt?: string;
  materialRoutingChangeNotice?: string;
  nextRetryAt?: string;
  providerStatus?: string;
  readAt?: string;
  safeDiagnosticSummary?: string;
}

export interface NotificationDeliveryAttempt {
  attemptedAt: string;
  channel: NotificationChannel;
  merchantSafeStatus: "FAILED" | "QUEUED" | "SENT" | "SKIPPED";
  message: string;
}

export interface NotificationListFilters {
  limit?: number;
  status?: NotificationStatus | "";
}

export interface NotificationListPage {
  notifications: MerchantNotification[];
  nextCursor: string | null;
  unreadCount: number;
  failedCount: number;
}

export interface NotificationPreferenceRule {
  eventType: NotificationEventType;
  channel: NotificationChannel;
  enabled: boolean;
  scope: NotificationPreferenceScope;
}

export interface NotificationPreferenceRecord {
  storeHash?: string;
  userId?: string;
  role?: string;
  rules: NotificationPreferenceRule[];
  recipients?: string[];
  updatedAt: string;
  updatedBy: string;
}

export interface NotificationPreferenceDraft {
  rules: NotificationPreferenceRule[];
  recipients: string;
  ownerRoutingAcknowledged: boolean;
}

export interface NotificationDeliveryFailure {
  notificationId: string;
  canRetry: boolean;
  channel: NotificationChannel;
  deliveryHistory: NotificationDeliveryAttempt[];
  subject: string;
  failedAt: string;
  lastRetryAt?: string;
  materialRoutingChangeNotice?: string;
  nextRetryAt?: string;
  providerStatus?: string;
  correctionPath: string;
  recipientProjection: string;
  retryAttempts: number;
  retryStatus: "FAILED" | "PENDING_RETRY";
  safeDiagnosticSummary?: string;
  safeMessage: string;
}

export const NOTIFICATION_EVENTS: NotificationEventType[] = [
  "REVIEW_REQUIRED",
  "REQUEST_EXPIRING_SOON",
  "PRICE_CHANGED",
  "STOCK_CHANGED",
  "MONITORING_FAILED",
  "INTEGRATION_ISSUE",
  "ORDER_ATTRIBUTED",
  "REFUND_ATTRIBUTED",
];

export const MERCHANT_VISIBLE_NOTIFICATION_EVENTS: NotificationEventType[] = [
  "REVIEW_REQUIRED",
  "REQUEST_EXPIRING_SOON",
  "MONITORING_FAILED",
  "INTEGRATION_ISSUE",
];

export const NOTIFICATION_CHANNELS: NotificationChannel[] = ["IN_APP", "EMAIL"];

export const MANDATORY_RULES: Array<Pick<NotificationPreferenceRule, "eventType" | "channel">> = [
  { eventType: "MONITORING_FAILED", channel: "IN_APP" },
  { eventType: "INTEGRATION_ISSUE", channel: "IN_APP" },
];

export function defaultNotificationFilters(): NotificationListFilters {
  return { limit: 10, status: "" };
}

export function defaultPreferenceDraft(): NotificationPreferenceDraft {
  return {
    rules: NOTIFICATION_EVENTS.flatMap((eventType) =>
      NOTIFICATION_CHANNELS.map((channel) => ({
        eventType,
        channel,
        enabled: isMandatoryRule(eventType, channel) || defaultEnabled(eventType),
        scope: "USER" as const,
      })),
    ),
    recipients: "",
    ownerRoutingAcknowledged: false,
  };
}

export function preferenceDraftFromRecord(
  record: NotificationPreferenceRecord,
): NotificationPreferenceDraft {
  const defaults = defaultPreferenceDraft();
  return {
    ...defaults,
    rules: mergeRules(defaults.rules, record.rules),
    recipients: record.recipients?.join(", ") ?? defaults.recipients,
  };
}

export function recordFromDraft(
  record: NotificationPreferenceRecord | undefined,
  draft: NotificationPreferenceDraft,
): NotificationPreferenceRecord {
  return {
    ...(record?.storeHash === undefined ? {} : { storeHash: record.storeHash }),
    ...(record?.userId === undefined ? {} : { userId: record.userId }),
    ...(record?.role === undefined ? {} : { role: record.role }),
    rules: draft.rules.map((rule) => ({
      ...rule,
      enabled: isMandatoryRule(rule.eventType, rule.channel) || rule.enabled,
    })),
    recipients: normalizeRecipients(draft.recipients),
    updatedAt: record?.updatedAt ?? new Date(0).toISOString(),
    updatedBy: record?.updatedBy ?? "local",
  };
}

export function merchantVisiblePreferenceRules(
  rules: NotificationPreferenceRule[],
): NotificationPreferenceRule[] {
  return rules.filter((rule) => isMerchantVisibleNotificationEvent(rule.eventType));
}

export function validatePreferenceDraft(draft: NotificationPreferenceDraft): string[] {
  return [
    ...validateMandatoryRules(draft.rules),
    ...validateRecipients(draft),
    ...validateOwnerAcknowledgement(draft),
  ];
}

export function deliveryFailureFor(
  notification: MerchantNotification,
  includePendingRetry = false,
): NotificationDeliveryFailure | undefined {
  if (
    notification.status !== "FAILED" &&
    !(includePendingRetry && notification.status === "PENDING")
  )
    return undefined;
  return {
    notificationId: notification.notificationId,
    canRetry: notification.status === "FAILED",
    channel: notification.channel,
    deliveryHistory: notification.deliveryHistory ?? defaultDeliveryHistory(notification),
    subject: notification.subject,
    failedAt: notification.createdAt,
    ...(notification.lastRetryAt === undefined ? {} : { lastRetryAt: notification.lastRetryAt }),
    ...(notification.materialRoutingChangeNotice === undefined
      ? {}
      : { materialRoutingChangeNotice: notification.materialRoutingChangeNotice }),
    ...(notification.nextRetryAt === undefined ? {} : { nextRetryAt: notification.nextRetryAt }),
    ...(notification.providerStatus === undefined
      ? {}
      : { providerStatus: notification.providerStatus }),
    correctionPath: "/settings?tab=notifications",
    recipientProjection: `${notification.channel} delivery for merchant user ${notification.recipientUserId}`,
    retryAttempts:
      notification.deliveryHistory?.length ?? (notification.status === "PENDING" ? 1 : 0),
    retryStatus: notification.status === "PENDING" ? "PENDING_RETRY" : "FAILED",
    ...(notification.safeDiagnosticSummary === undefined
      ? {}
      : { safeDiagnosticSummary: notification.safeDiagnosticSummary }),
    safeMessage:
      notification.status === "PENDING"
        ? "Delivery retry is queued. Keep routing settings valid while the backend processes the attempt."
        : "Delivery failed. Review recipient routing before retrying or correcting the notification setup.",
  };
}

export function isMandatoryRule(
  eventType: NotificationEventType,
  channel: NotificationChannel,
): boolean {
  return MANDATORY_RULES.some((rule) => rule.eventType === eventType && rule.channel === channel);
}

export function isMerchantVisibleNotificationEvent(eventType: NotificationEventType): boolean {
  return MERCHANT_VISIBLE_NOTIFICATION_EVENTS.includes(eventType);
}

function mergeRules(
  defaultRules: NotificationPreferenceRule[],
  storedRules: NotificationPreferenceRule[],
): NotificationPreferenceRule[] {
  const stored = new Map(storedRules.map((rule) => [ruleKey(rule), rule]));
  return defaultRules.map((rule) => ({
    ...rule,
    ...(stored.get(ruleKey(rule)) ?? {}),
    enabled:
      isMandatoryRule(rule.eventType, rule.channel) ||
      (stored.get(ruleKey(rule))?.enabled ?? rule.enabled),
  }));
}

function ruleKey(
  rule: Pick<NotificationPreferenceRule, "eventType" | "channel" | "scope">,
): string {
  return `${rule.eventType}:${rule.channel}:${rule.scope}`;
}

function defaultEnabled(eventType: NotificationEventType): boolean {
  return MERCHANT_VISIBLE_NOTIFICATION_EVENTS.includes(eventType);
}

function validateMandatoryRules(rules: NotificationPreferenceRule[]): string[] {
  return rules
    .filter((rule) => isMandatoryRule(rule.eventType, rule.channel) && !rule.enabled)
    .map((rule) => `${rule.eventType} ${rule.channel} is mandatory and cannot be disabled.`);
}

function validateRecipients(draft: NotificationPreferenceDraft): string[] {
  const emailEnabled = draft.rules.some(
    (rule) =>
      isMerchantVisibleNotificationEvent(rule.eventType) &&
      rule.channel === "EMAIL" &&
      rule.enabled,
  );
  if (!emailEnabled) return [];
  const recipients = normalizeRecipients(draft.recipients);
  if (recipients.length === 0)
    return ["At least one recipient is required when email notifications are enabled."];
  return recipients.every((recipient) => /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(recipient))
    ? []
    : ["Every recipient must be a valid email address."];
}

function validateOwnerAcknowledgement(draft: NotificationPreferenceDraft): string[] {
  const ownerMaterialEventsDisabled = draft.rules.some(
    (rule) =>
      isMerchantVisibleNotificationEvent(rule.eventType) &&
      rule.channel === "EMAIL" &&
      !rule.enabled,
  );
  return ownerMaterialEventsDisabled && !draft.ownerRoutingAcknowledged
    ? ["Owner acknowledgement is required for material routing changes."]
    : [];
}

function defaultDeliveryHistory(notification: MerchantNotification): NotificationDeliveryAttempt[] {
  return [
    {
      attemptedAt: notification.createdAt,
      channel: notification.channel,
      merchantSafeStatus: notification.status === "FAILED" ? "FAILED" : "QUEUED",
      message:
        notification.status === "FAILED"
          ? "Delivery failed. Provider-specific diagnostics are hidden from merchant UI."
          : "Delivery retry is queued.",
    },
  ];
}

function normalizeRecipients(value: string): string[] {
  return value
    .split(",")
    .map((item) => item.trim().toLowerCase())
    .filter(Boolean);
}
